/**
 * Licensed Materials - Property of IBM
 * IBM Business Analytics (C) Copyright IBM Corp. 2019
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */

/**
 * @class ServiceabilityRenderingInfoImpl
 * @hideconstructor
 * @classdesc Render Service Implementation class
 */
define([
	'../../../../api/impl/serviceability/nls/StringResources'
], (StringResources) => {
	class ServiceabilityRenderingInfo {
		constructor(logger) {
			this._logger = logger;
			this._renderingInfo = undefined;
		}

		/**
		 * @returns {RenderingInfo} the current rendering info object
		 */
		getCurrentRendering() {
			return this._renderingInfo;
		}

		/**
		 * @param {RenderingInfo} renderingInfo the info of a rendersequence which is being executed
		 */
		setCurrentRendering(renderingInfo) {
			this._renderingInfo = renderingInfo;
		}

		/**
		 * @returns {RenderingInfo} A RenderingInfo object represent the latest valid rendering info
		 */
		getLastRendering() {
			return this._lastRendering;
		}

		/**
		 * @description set the latest valid rendering info object
		 * @param {RenderingInfo} lastRendering An object of class RenderingInfo
		 */
		setLastRendering(lastRendering) {
			this._lastRendering = lastRendering;
		}

		toJSON() {
			return {
				renderingInfo: {
					current: (this._renderingInfo ? this._renderingInfo.toJSON() : {}),
					last: (this._lastRendering ? this._lastRendering.toJSON() : {})
				}
			};
		}

		/**
		 * @description Returns a json which currently will be consumed by Serviceability feature
		 * @returns a json represent the desired UI
		 */
		toUIJSON() {
			let request_durations = [], total_duration;
			if(this.getLastRendering() === undefined) {
				total_duration = this.getCurrentRendering().getDuration();
				let taskInfo = this.getCurrentRendering().getRenderSequenceWithData();
				if (taskInfo === undefined) {
					total_duration = total_duration + ' ' + StringResources.get('renderInProgress');
				} else {
					request_durations = taskInfo.getTaskInfo('data').getRequestDurations();
					total_duration = total_duration + ' ' + StringResources.get('renderInProgress');
				}
			} else {
				total_duration = this.getLastRendering().getDuration();
				let taskInfo = this.getLastRendering().getRenderSequenceWithData();
				request_durations = taskInfo.getTaskInfo('data').getRequestDurations();
			}

			let cellData = [
				[ StringResources.get('liveWidgetTotalTime'), total_duration ]
			];
			this._addRequestDurations(cellData, request_durations);

			return [ {
				sectionName: StringResources.get('performanceSection'),
				sectionValues: [{
					fieldName: StringResources.get('durationField'),
					fieldValue: {
						cellValue: (row, col) => { return cellData[row][col]; },
						columnLength: 2,
						rowLength: cellData.length
					},
					type: 'SimpleTable'
				}]
			}];
		}

		_addRequestDurations(cellData, requestDurations) {
			if (requestDurations.length === 0) {
				cellData.push([StringResources.get('singleQueryTime'), StringResources.get('infoNotAvailable') ]);
			} else {
				requestDurations.forEach((duration, index) => {
					let label = '';
					if (requestDurations.length === 1) {
						label = StringResources.get('singleQueryTime');
					} else {
						label = StringResources.get('multipleQueryTime', { name: index + 1 });
					}
					if (isNaN(duration)) {
						cellData.push([label, StringResources.get('infoNotAvailable') ]);
					} else {
						cellData.push([label, duration ]);
					}
				});
			}
			return cellData;
		}
	}
	return ServiceabilityRenderingInfo;
});
